import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import PaymentIcon from '@mui/icons-material/Payment';
import DeleteIcon from '@mui/icons-material/Delete';
import ConstructionIcon from '@mui/icons-material/Construction';
import PersonIcon from '@mui/icons-material/Person';
import { projectsApi, ownersApi } from '../../services/mybudgetApi';
import {
  ProjectDetail,
  ProjectSummary,
  Owner,
} from '../../types';
import { useCategories } from '../../contexts/CategoriesContext';
import { Card } from '../../components/shared/Card';
import { Loading } from '../../components/shared/Loading';
import { ActionButton } from '../../components/shared/ActionButton';
import { Input } from '../../components/shared/Input';
import { Select } from '../../components/shared/Select';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { CategoryBadge } from '../../components/shared/CategoryBadge';
import { useToaster } from '../../components/shared/Toaster';
import { formatCurrency } from '../../constants';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function ProgressBar({ percent }: { percent: number }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-dark-border">
      <div
        className="h-full rounded-full bg-accent transition-all"
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  );
}

function ProjectCard({
  project,
  onPay,
  onOpen,
}: {
  project: ProjectSummary;
  onPay: () => void;
  onOpen: () => void;
}) {
  const isActive = project.status === 'active';

  return (
    <Card className="!p-0 transition hover:border-accent/30">
      <button onClick={onOpen} className="w-full px-5 py-4 text-left">
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-gray-100">{project.name}</h3>
              {!isActive && (
                <span className="rounded-full bg-income/15 px-2 py-0.5 text-xs text-income">
                  Quitado
                </span>
              )}
            </div>
            <p className="shrink-0 text-xl font-bold text-gray-100">
              {project.progressPercent.toFixed(0)}%
            </p>
          </div>
          <CategoryBadge category={project.category} />
          {project.owner && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
              <PersonIcon style={{ fontSize: 14 }} />
              {project.owner.name}
            </span>
          )}
          {project.description && (
            <p className="text-sm text-gray-500 line-clamp-2">{project.description}</p>
          )}
          <ProgressBar percent={project.progressPercent} />
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-xs text-gray-500">Pago</p>
              <p className="font-medium text-income">{formatCurrency(project.paidAmount)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Restante</p>
              <p className={`font-medium ${isActive ? 'text-amber-400' : 'text-gray-500'}`}>
                {formatCurrency(project.remainingAmount)}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Total {formatCurrency(project.totalAmount)} · {project.paymentsCount} pag.
          </p>
        </div>
      </button>
      {isActive && (
        <div className="border-t border-dark-border px-5 py-3">
          <ActionButton
            variant="secondary"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation();
              onPay();
            }}
          >
            <PaymentIcon fontSize="small" />
            Registrar pagamento
          </ActionButton>
        </div>
      )}
    </Card>
  );
}

export function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerFilter = searchParams.get('ownerId')
    ? Number(searchParams.get('ownerId'))
    : undefined;

  const { categories, getDefaultCategoryId } = useCategories();
  const [owners, setOwners] = useState<Owner[]>([]);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [selected, setSelected] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [projectModal, setProjectModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToaster();

  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    categoryId: 0,
    ownerId: '' as string,
    totalAmount: '',
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paidOn: todayISO(),
    notes: '',
  });

  const [payingProjectId, setPayingProjectId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [proj, own] = await Promise.all([
        projectsApi.list(ownerFilter),
        ownersApi.list(),
      ]);
      setProjects(proj);
      setOwners(own);
    } finally {
      setLoading(false);
    }
  }, [ownerFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const setOwnerFilter = (ownerId: number | undefined) => {
    if (ownerId) {
      setSearchParams({ ownerId: String(ownerId) });
    } else {
      setSearchParams({});
    }
  };

  const openDetail = async (id: number) => {
    try {
      setSelected(await projectsApi.get(id));
      setDetailModal(true);
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao carregar',
      });
    }
  };

  const handleCreateProject = async () => {
    if (!projectForm.name || !projectForm.totalAmount) return;
    setSaving(true);
    try {
      await projectsApi.create({
        name: projectForm.name,
        description: projectForm.description || undefined,
        categoryId: projectForm.categoryId,
        ownerId: projectForm.ownerId ? Number(projectForm.ownerId) : null,
        totalAmount: Number(projectForm.totalAmount),
      });
      addToast({ type: 'success', message: 'Projeto criado!' });
      setProjectModal(false);
      setProjectForm({
        name: '',
        description: '',
        categoryId: getDefaultCategoryId() ?? categories[0]?.id ?? 0,
        ownerId: ownerFilter ? String(ownerFilter) : '',
        totalAmount: '',
      });
      load();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao salvar',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddPayment = async () => {
    if (!payingProjectId || !paymentForm.amount) return;
    setSaving(true);
    try {
      await projectsApi.addPayment(payingProjectId, {
        amount: Number(paymentForm.amount),
        paidOn: paymentForm.paidOn,
        notes: paymentForm.notes || undefined,
      });
      addToast({ type: 'success', message: 'Pagamento registrado!' });
      setPaymentModal(false);
      setPaymentForm({ amount: '', paidOn: todayISO(), notes: '' });
      setPayingProjectId(null);
      load();
      if (detailModal && selected?.id === payingProjectId) {
        setSelected(await projectsApi.get(payingProjectId));
      }
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao registrar',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayment = async (projectId: number, paymentId: number) => {
    if (!window.confirm('Remover este pagamento?')) return;
    try {
      await projectsApi.deletePayment(projectId, paymentId);
      addToast({ type: 'success', message: 'Pagamento removido' });
      setSelected(await projectsApi.get(projectId));
      load();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao remover',
      });
    }
  };

  const handleUpdateCategory = async (projectId: number, categoryId: number) => {
    setSaving(true);
    try {
      await projectsApi.update(projectId, { categoryId });
      addToast({ type: 'success', message: 'Categoria atualizada!' });
      if (selected?.id === projectId) {
        setSelected(await projectsApi.get(projectId));
      }
      load();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao atualizar',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOwner = async (projectId: number, ownerId: number | null) => {
    setSaving(true);
    try {
      await projectsApi.update(projectId, { ownerId });
      addToast({ type: 'success', message: 'Credor atualizado!' });
      if (selected?.id === projectId) {
        setSelected(await projectsApi.get(projectId));
      }
      load();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao atualizar',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async (id: number) => {
    if (!window.confirm('Remover este projeto e todos os pagamentos?')) return;
    try {
      await projectsApi.delete(id);
      addToast({ type: 'success', message: 'Projeto removido' });
      setDetailModal(false);
      setSelected(null);
      load();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao remover',
      });
    }
  };

  const activeProjects = projects.filter((p) => p.status === 'active');
  const totalOutstanding = activeProjects.reduce((s, p) => s + p.remainingAmount, 0);

  const openPayment = (project: ProjectSummary) => {
    setPayingProjectId(project.id);
    setPaymentForm({
      amount: project.remainingAmount > 0 ? String(project.remainingAmount) : '',
      paidOn: todayISO(),
      notes: '',
    });
    setPaymentModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-100">Projetos & Dívidas</h2>
          <p className="text-sm text-gray-400">
            Obra, serviços e pagamentos em datas diferentes
          </p>
        </div>
        <ActionButton onClick={() => {
          setProjectForm({
            name: '',
            description: '',
            categoryId: getDefaultCategoryId() ?? categories[0]?.id ?? 0,
            ownerId: ownerFilter ? String(ownerFilter) : '',
            totalAmount: '',
          });
          setProjectModal(true);
        }}>
          <AddIcon fontSize="small" />
          Novo projeto
        </ActionButton>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Select
            label="Filtrar por credor"
            options={[
              { value: '', label: 'Todos os credores' },
              ...owners.map((o) => ({ value: o.id, label: o.name })),
            ]}
            value={ownerFilter ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              setOwnerFilter(val ? Number(val) : undefined);
            }}
          />
        </div>
        {ownerFilter && (
          <button
            type="button"
            onClick={() => setOwnerFilter(undefined)}
            className="text-sm text-link hover:underline"
          >
            Limpar filtro
          </button>
        )}
      </div>

      {activeProjects.length > 0 && (
        <Card>
          <div className="flex items-center gap-3">
            <ConstructionIcon className="text-amber-400" />
            <div>
              <p className="text-sm text-gray-400">Saldo devedor em projetos ativos</p>
              <p className="text-2xl font-bold text-amber-400">
                {formatCurrency(totalOutstanding)}
              </p>
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <Loading />
      ) : projects.length === 0 ? (
        <Card>
          <p className="text-center text-gray-500">
            Nenhum projeto ainda. Exemplo: pedreiro — R$ 1.000,00 com pagamentos de R$ 100 hoje e R$ 900 na próxima semana.
          </p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onPay={() => openPayment(project)}
              onOpen={() => openDetail(project.id)}
            />
          ))}
        </div>
      )}

      <Modal
        open={projectModal}
        onClose={() => setProjectModal(false)}
        title="Novo projeto"
        footer={
          <ModalActions
            onCancel={() => setProjectModal(false)}
            onConfirm={handleCreateProject}
            loading={saving}
          />
        }
      >
        <div className="space-y-4">
          <Input
            label="Nome (ex: Pedreiro — reforma banheiro)"
            value={projectForm.name}
            onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
            required
          />
          <Input
            label="Descrição (opcional)"
            value={projectForm.description}
            onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
          />
          <Select
            label="Categoria"
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            value={projectForm.categoryId}
            onChange={(e) =>
              setProjectForm({ ...projectForm, categoryId: Number(e.target.value) })
            }
          />
          <Select
            label="Credor"
            options={[
              { value: '', label: 'Nenhum / não informado' },
              ...owners.map((o) => ({ value: o.id, label: o.name })),
            ]}
            value={projectForm.ownerId}
            onChange={(e) => setProjectForm({ ...projectForm, ownerId: e.target.value })}
          />
          <Input
            label="Valor total do projeto (R$)"
            type="number"
            step="0.01"
            min="0"
            value={projectForm.totalAmount}
            onChange={(e) => setProjectForm({ ...projectForm, totalAmount: e.target.value })}
            required
          />
        </div>
      </Modal>

      <Modal
        open={paymentModal}
        onClose={() => setPaymentModal(false)}
        title="Registrar pagamento"
        footer={
          <ModalActions
            onCancel={() => setPaymentModal(false)}
            onConfirm={handleAddPayment}
            confirmLabel="Registrar"
            loading={saving}
          />
        }
      >
        <div className="space-y-4">
          <Input
            label="Valor pago (R$)"
            type="number"
            step="0.01"
            min="0"
            value={paymentForm.amount}
            onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
            required
          />
          <Input
            label="Data do pagamento"
            type="date"
            value={paymentForm.paidOn}
            onChange={(e) => setPaymentForm({ ...paymentForm, paidOn: e.target.value })}
            required
          />
          <Input
            label="Observações (opcional)"
            value={paymentForm.notes}
            onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
          />
          <p className="text-xs text-gray-500">
            O pagamento será contabilizado como despesa no mês da data informada.
          </p>
        </div>
      </Modal>

      <Modal
        open={detailModal}
        onClose={() => setDetailModal(false)}
        title={selected?.name ?? 'Projeto'}
        footer={
          selected && (
            <div className="flex w-full justify-between">
              <ActionButton
                variant="danger"
                onClick={() => handleDeleteProject(selected.id)}
              >
                Excluir projeto
              </ActionButton>
              {selected.status === 'active' && (
                <ActionButton
                  onClick={() => {
                    setDetailModal(false);
                    openPayment(selected);
                  }}
                >
                  <PaymentIcon fontSize="small" />
                  Novo pagamento
                </ActionButton>
              )}
            </div>
          )
        }
      >
        {selected && (
          <div className="space-y-5">
            <Select
              label="Categoria"
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
              value={selected.categoryId}
              disabled={saving}
              onChange={(e) =>
                handleUpdateCategory(selected.id, Number(e.target.value))
              }
            />
            <Select
              label="Credor"
              options={[
                { value: '', label: 'Nenhum / não informado' },
                ...owners.map((o) => ({ value: o.id, label: o.name })),
              ]}
              value={selected.ownerId ?? ''}
              disabled={saving}
              onChange={(e) => {
                const val = e.target.value;
                handleUpdateOwner(selected.id, val ? Number(val) : null);
              }}
            />

            <div className="space-y-2">
              <ProgressBar percent={selected.progressPercent} />
              <div className="grid grid-cols-3 gap-2 text-center text-xs sm:gap-3 sm:text-sm">
                <div className="min-w-0">
                  <p className="text-gray-500">Total</p>
                  <p className="truncate font-semibold">{formatCurrency(selected.totalAmount)}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-gray-500">Pago</p>
                  <p className="truncate font-semibold text-income">
                    {formatCurrency(selected.paidAmount)}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-gray-500">Restante</p>
                  <p className="truncate font-semibold text-amber-400">
                    {formatCurrency(selected.remainingAmount)}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="mb-3 font-medium text-gray-300">Histórico de pagamentos</h4>
              {selected.payments.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum pagamento registrado</p>
              ) : (
                <div className="space-y-2">
                  {selected.payments.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between rounded-xl border border-dark-border bg-dark-bg/50 px-4 py-3"
                    >
                      <div>
                        <p className="font-medium text-income">
                          {formatCurrency(p.amount)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(p.paidOn + 'T12:00:00').toLocaleDateString('pt-BR')}
                          {p.notes && ` — ${p.notes}`}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeletePayment(selected.id, p.id)}
                        className="text-gray-500 hover:text-red-400"
                      >
                        <DeleteIcon fontSize="small" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
